// src/pages/NorthStar.js

/**
 * NorthStar — Mission and impact hub.
 * Shows live Firestore stats, real-time impact feed, quick actions,
 * and embeds the Feature Library (Features / Why / Career tabs).
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { db } from "../lib/firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import {
  Users2,
  Package,
  Activity,
  Clock,
  WifiOff,
  BookOpen,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import FeatureLibrary from "../components/FeatureLibrary";
import { formatImpactFeedText, formatImpactFeedTime } from "../utils/impactFeedFormatter";
import { errorDev } from '../utils/logger.js';
import { useTranslation } from "react-i18next";

// ─── Stat Card ──────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4 flex items-center gap-3">
      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
          {label}
        </div>
        <div className="text-lg font-bold text-gray-900 dark:text-white">
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
      </div>
    </div>
  );
}

// ─── Impact Feed Item ───────────────────────────────────────────
function ImpactFeedItem({ item }) {
  const getIcon = (type) => {
    switch (type) {
        case "claim":
        case "listing":
          return <Package className="w-3.5 h-3.5 text-green-600" />;
        default:
          return <Activity className="w-3.5 h-3.5 text-green-600" />;
    }
  };
  const text = formatImpactFeedText(item);
  const time = formatImpactFeedTime(item?.timestamp);

  return (
    <li className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-neutral-800/60 border border-gray-100 dark:border-neutral-700/50">
      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-green-50 dark:bg-green-900/20 mt-0.5 flex-shrink-0">
        {getIcon(item.type)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
          {text}
        </p>
        <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
          <Clock className="w-3 h-3" />
          {time}
        </span>
      </div>
    </li>
  );
}

// ─── Quick Action Button ────────────────────────────────────────
function QuickAction({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 text-sm font-medium text-gray-900 dark:text-white hover:border-green-500 dark:hover:border-green-500 transition text-center"
    >
      {label}
    </button>
  );
}

// ─── Main ───────────────────────────────────────────────────────
export default function NorthStar() {
  const navigate = useNavigate();
  const { t } = useTranslation(["common"]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Live counts from Firestore
  const [counts, setCounts] = useState({ users: 0, products: 0, impact: 0 });
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Impact feed
  const [impactFeed, setImpactFeed] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Online/offline tracking
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Fetch collection counts
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingCounts(true);
      try {
        const names = ["users", "products", "impactFeed"];
        const results = {};
        for (const name of names) {
          try {
            const snap = await getDocs(collection(db, name));
            results[name] = snap.size || 0;
          } catch (err) {
            errorDev("Operation failed:", err);
            results[name] = 0;
          }
        }
        if (!mounted) return;
        setCounts({
          users: results.users,
          products: results.products,
          impact: results.impactFeed,
        });
      } finally {
        if (mounted) setLoadingCounts(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Real-time impact feed listener
  useEffect(() => {
    const q = query(
      collection(db, "impactFeed"),
      orderBy("timestamp", "desc"),
      limit(8)
    );
    setLoadingFeed(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setImpactFeed(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingFeed(false);
      },
      () => {
        setImpactFeed([]);
        setLoadingFeed(false);
      }
    );
    return () => unsub();
  }, []);

  const stats = useMemo(
    () => [
      { key: "users", label: "Community Members", icon: Users2, value: loadingCounts ? "..." : counts.users },
      { key: "products", label: "Products Listed", icon: Package, value: loadingCounts ? "..." : counts.products },
      { key: "impact", label: "Impact Events", icon: Activity, value: loadingCounts ? "..." : counts.impact },
    ],
    [counts, loadingCounts]
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Sticky header - matches MoreNew pattern */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex h-12 max-w-7xl items-center gap-2 px-3 sm:px-4">
          <Link
            to="/me"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
            aria-label={t("common:back", "Back")}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">
            {t("common:northStar", "North Star")}
          </h1>
        </div>
        {!libraryOpen && (
          <div className="mx-auto max-w-7xl px-3 sm:px-4 pb-2">
            <div className="flex items-center justify-end">
              <button
                onClick={() => setLibraryOpen(true)}
                className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                aria-label={t("common:openLibrary", "Open Library")}
              >
                <BookOpen className="w-5 h-5 text-green-700 dark:text-green-400" />
              </button>
            </div>
          </div>
        )}
        {libraryOpen && (
          <div className="mx-auto max-w-7xl px-3 sm:px-4 pb-2">
            <div className="flex items-center justify-end">
              <button
                onClick={() => setLibraryOpen(false)}
                className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                aria-label={t("common:closeLibrary", "Close Library")}
              >
                <BookOpen className="w-5 h-5 text-green-700 dark:text-green-400" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Desktop banner */}
      <div className="hidden md:block bg-gradient-to-r from-green-800 via-green-700 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="ns-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ns-grid)" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 relative z-10">
          <div className="text-center mb-4">
            <h2 className="text-lg font-bold text-white">
              Where Technology Meets Humanity's Oldest Need
            </h2>
            <p className="text-sm text-white/80 mt-1 max-w-lg mx-auto">
              Live data from the Orrith - Cycle of Certainty network. Every number here represents a real person, a real harvest, a real meal.
            </p>
          </div>
          <div className="flex justify-center">
            <button
              onClick={() => setLibraryOpen(!libraryOpen)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition border border-white/20"
            >
              <BookOpen className="w-4 h-4" />
              Orrith - Cycle of Certainty Library
              {libraryOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Library section (toggled) */}
      {libraryOpen && (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-6">
          <FeatureLibrary />
        </div>
      )}

      {/* Content */}
      <main className={`max-w-7xl mx-auto px-3 sm:px-4 py-4 space-y-6 ${libraryOpen ? "hidden md:block" : ""}`}>
        {/* Connectivity warning */}
        {!isOnline && (
          <div className="p-2.5 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 inline-flex items-center gap-2 text-xs">
            <WifiOff className="w-3.5 h-3.5" />
            You are offline. Some data may not be current.
          </div>
        )}

        {/* Stats grid */}
        <section>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.map((stat) => (
              <StatCard key={stat.key} icon={stat.icon} label={stat.label} value={stat.value} />
            ))}
          </div>
        </section>

        {/* Two-column: Mission + Impact Feed */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Mission statement */}
          <div className="lg:col-span-2 bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
              What Orrith - Cycle of Certainty Is
            </h3>
            <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <p>
                Orrith - Cycle of Certainty is a food system infrastructure built for humanity. It connects
                smallholder farmers to local buyers, tracks community impact with honest numbers, and gives
                farmers market intelligence so they never plant blind again.
              </p>
              <p>
                Every feature was born from a moment we witnessed: a farmer hauling unsold
                produce home, a community with no visibility into its own surplus. Orrith - Cycle of Certainty
                closes those gaps with technology that is simple enough for a basic phone, honest enough to
                trust, and built to last decades.
              </p>
              <p>
                This page is your live view into the network. The numbers above are pulled
                directly from our database. The impact feed to the right updates in real time.
                Nothing here is estimated or projected. It is the living record of communities
                trading and tracking their collective impact.
              </p>
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2 mt-5">
              <QuickAction label="List Your Produce" onClick={() => navigate("/sellnow")} />
              <QuickAction label="Browse Marketplace" onClick={() => navigate("/farm")} />
              <QuickAction label="View Impact" onClick={() => navigate("/farmmark")} />
            </div>
          </div>

          {/* Live impact feed */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                Impact Feed
              </h3>
              <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Live
              </span>
            </div>

            <div className="space-y-2 max-h-80 lg:max-h-[28rem] overflow-y-auto">
              {loadingFeed ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-14 bg-gray-100 dark:bg-neutral-700 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : impactFeed.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No impact events yet. Be the first.</p>
                </div>
              ) : (
                <AnimatePresence>
                  <ul className="space-y-2">
                    {impactFeed.map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ImpactFeedItem item={item} />
                      </motion.div>
                    ))}
                  </ul>
                </AnimatePresence>
              )}
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}



